import * as XLSX from 'xlsx';
import {
  UploadedDailyData,
  UploadedWeeklyData,
  UploadedMonthlyData,
  UploadedOfflineData,
  UploadedKpiData,
  UploadedDataResponse,
} from 'src/types';

/**
 * Parse Excel file and convert to UploadedDataResponse format
 */
export const parseExcelFile = async (
  file: File,
): Promise<UploadedDataResponse['data']> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = e.target?.result;
        if (!data) {
          throw new Error('Failed to read file');
        }

        const workbook = XLSX.read(data, { type: 'binary' });

        // Initialize data structure
        const result: UploadedDataResponse['data'] = {
          fileInfo: {
            fileId: `preview-${Date.now()}`,
            fileKey: `preview/${file.name}`,
            fileName: file.name,
            status: 'succeeded',
            uploadedAt: new Date().toISOString(),
          },
          dailyData: [],
          weeklyData: [],
          monthlyData: [],
          offlineData: [],
          kpiData: [],
          summary: {
            totalRecords: 0,
            dateRange: {},
            dataTypes: [],
          },
        };

        // Parse each sheet
        workbook.SheetNames.forEach((sheetName) => {
          const sheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(sheet);

          if (jsonData.length === 0) return;

          // Determine data type by sheet name or first row content
          const normalizedSheetName = sheetName.toLowerCase().trim();
          const firstRow = jsonData[0] as Record<string, unknown>;

          // Check for specific sheet names first
          if (
            normalizedSheetName.includes('daily') ||
            normalizedSheetName.includes('data_daily') ||
            normalizedSheetName === 'daily digital'
          ) {
            result.dailyData = parseDailyData(jsonData, file.name);
            result.summary.dataTypes.push('daily');
          } else if (
            normalizedSheetName.includes('weekly') ||
            normalizedSheetName.includes('data_weekly') ||
            normalizedSheetName === 'weekly digital'
          ) {
            result.weeklyData = parseWeeklyData(jsonData, file.name);
            result.summary.dataTypes.push('weekly');
          } else if (
            normalizedSheetName.includes('monthly') ||
            normalizedSheetName.includes('data_monthly') ||
            normalizedSheetName === 'monthly digital'
          ) {
            result.monthlyData = parseMonthlyData(jsonData, file.name);
            result.summary.dataTypes.push('monthly');
          } else if (
            normalizedSheetName.includes('offline') ||
            normalizedSheetName.includes('data_offline') ||
            normalizedSheetName === 'offline media'
          ) {
            result.offlineData = parseOfflineData(jsonData, file.name);
            result.summary.dataTypes.push('offline');
          } else if (
            normalizedSheetName.includes('kpi') ||
            normalizedSheetName.includes('data_kpi')
          ) {
            result.kpiData = parseKpiData(jsonData, file.name);
            result.summary.dataTypes.push('kpi');
          } else {
            // If no specific sheet name, try to determine by column content
            // Use more flexible column name matching
            const hasDate = hasColumn(firstRow, [
              'date',
              'Date',
              'DATE',
              'dt',
              'Dt',
              'DT',
              'date_time',
              'dateTime',
              'Date Time',
            ]);
            const hasWeekStartDate = hasColumn(firstRow, [
              'weekStartDate',
              'Week Start Date',
              'week_start_date',
              'week start',
              'Week Start',
              'weekStart',
              'WeekStart',
              'start_of_week',
              'Start of Week',
              'Week Start', // Your actual column name
            ]);
            const hasMonthStartDate = hasColumn(firstRow, [
              'monthStartDate',
              'Month Start Date',
              'month_start_date',
              'month start',
              'Month Start',
              'monthStart',
              'MonthStart',
              'start_of_month',
              'Start of Month',
              'Month', // Your actual column name
            ]);
            const hasSales = hasColumn(firstRow, [
              'sales',
              'Sales',
              'SALES',
              'revenue',
              'Revenue',
              'REVENUE',
              'total_sales',
              'Total Sales',
              'totalSales',
            ]);
            const hasUnitsSold = hasColumn(firstRow, [
              'unitsSold',
              'Units Sold',
              'units_sold',
              'quantity',
              'Quantity',
              'QUANTITY',
              'units',
              'Units',
              'UNITS',
            ]);
            const hasTRPs = hasColumn(firstRow, [
              'trps',
              'TRPs',
              'TRP',
              'trp',
              'rating_points',
              'Rating Points',
              'target_rating_points',
              'Target Rating Points',
              'TRPs', // Your actual column name
            ]);
            const hasSpend = hasColumn(firstRow, [
              'spend',
              'Spend',
              'SPEND',
              'cost',
              'Cost',
              'COST',
              'budget',
              'Budget',
              'BUDGET',
              'expense',
              'Expense',
              'EXPENSE',
              'Spend', // Your actual column name
            ]);

            // Priority order: KPI > Offline > Weekly > Monthly > Daily
            if (hasSales || hasUnitsSold) {
              // KPI data - highest priority
              result.kpiData = parseKpiData(jsonData, file.name);
              result.summary.dataTypes.push('kpi');
            } else if (hasTRPs) {
              // Offline data
              result.offlineData = parseOfflineData(jsonData, file.name);
              result.summary.dataTypes.push('offline');
            } else if (hasWeekStartDate) {
              // Weekly data
              result.weeklyData = parseWeeklyData(jsonData, file.name);
              result.summary.dataTypes.push('weekly');
            } else if (hasMonthStartDate) {
              // Monthly data
              result.monthlyData = parseMonthlyData(jsonData, file.name);
              result.summary.dataTypes.push('monthly');
            } else if (hasDate && hasSpend) {
              // Daily data - must have both date and spend columns
              result.dailyData = parseDailyData(jsonData, file.name);
              result.summary.dataTypes.push('daily');
            } else if (hasDate) {
              // Fallback: if only has date, try to parse as daily anyway
              result.dailyData = parseDailyData(jsonData, file.name);
              result.summary.dataTypes.push('daily');
            }
          }
        });

        // Calculate summary
        result.summary.totalRecords =
          result.dailyData.length +
          result.weeklyData.length +
          result.monthlyData.length +
          result.offlineData.length +
          result.kpiData.length;

        // Calculate date range
        const allDates: Date[] = [];

        result.dailyData.forEach((d) => {
          if (d.date) allDates.push(new Date(d.date));
        });
        result.weeklyData.forEach((d) => {
          if (d.weekStartDate) allDates.push(new Date(d.weekStartDate));
        });
        result.monthlyData.forEach((d) => {
          if (d.monthStartDate) allDates.push(new Date(d.monthStartDate));
        });
        result.offlineData.forEach((d) => {
          if (d.periodStartDate) allDates.push(new Date(d.periodStartDate));
        });
        result.kpiData.forEach((d) => {
          if (d.date) allDates.push(new Date(d.date));
        });

        if (allDates.length > 0) {
          const validDates = allDates.filter((d) => !isNaN(d.getTime()));
          if (validDates.length > 0) {
            validDates.sort((a, b) => a.getTime() - b.getTime());
            result.summary.dateRange.earliest = validDates[0]
              .toISOString()
              .split('T')[0];
            result.summary.dateRange.latest = validDates[validDates.length - 1]
              .toISOString()
              .split('T')[0];
          }
        }

        resolve(result);
      } catch (error) {
        reject(error);
      }
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    reader.readAsBinaryString(file);
  });
};

/**
 * Parse daily data from Excel rows
 */
const parseDailyData = (
  rows: unknown[],
  fileName: string,
): UploadedDailyData[] => {
  return (rows as Record<string, unknown>[])
    .map((row, index) => {
      const date = parseDate(row.Date || row.date);
      if (!date) return null;

      return {
        date: date,
        campaignId: String(
          row['Campaign ID'] || row.campaignId || `campaign-${index}`,
        ),
        campaignName: String(
          row['Campaign Name'] || row.campaignName || 'Unknown Campaign',
        ),
        mediaGroup: row['Media Group'] || row.mediaGroup,
        media: row.Media || row.media,
        impressions: parseNumber(row.Impressions || row.impressions),
        spend: parseNumber(row.Spend || row.spend) || 0,
        source: row.Source || row.source || fileName,
        lineItemName: row['Line Item'] || row.lineItemName,
        fileId: `preview-${Date.now()}`,
      } as UploadedDailyData;
    })
    .filter((item): item is UploadedDailyData => item !== null);
};

/**
 * Parse weekly data from Excel rows
 */
const parseWeeklyData = (
  rows: unknown[],
  fileName: string,
): UploadedWeeklyData[] => {
  return (rows as Record<string, unknown>[])
    .map((row, index) => {
      const weekStartDate = parseDate(
        row['Week Start'] ||
          row['Week Start Date'] ||
          row.weekStartDate ||
          row.Date ||
          row.date,
      );
      if (!weekStartDate) return null;

      return {
        weekStartDate: weekStartDate,
        campaignId: String(
          row['Campaign ID'] || row.campaignId || `campaign-${index}`,
        ),
        campaignName: String(
          row['Campaign Name'] || row.campaignName || 'Unknown Campaign',
        ),
        mediaGroup: row['Media Group'] || row.mediaGroup,
        media: row.Media || row.media,
        impressions: parseNumber(row.Impressions || row.impressions),
        spend: parseNumber(row.Spend || row.spend) || 0,
        source: row.Source || row.source || fileName,
        lineItemName: row['Line Item'] || row.lineItemName,
        fileId: `preview-${Date.now()}`,
      } as UploadedWeeklyData;
    })
    .filter((item): item is UploadedWeeklyData => item !== null);
};

/**
 * Parse monthly data from Excel rows
 */
const parseMonthlyData = (
  rows: unknown[],
  fileName: string,
): UploadedMonthlyData[] => {
  return (rows as Record<string, unknown>[])
    .map((row, index) => {
      const monthStartDate = parseDate(
        row['Month'] ||
          row['Month Start Date'] ||
          row.monthStartDate ||
          row.Date ||
          row.date,
      );
      if (!monthStartDate) return null;

      return {
        monthStartDate: monthStartDate,
        campaignId: String(
          row['Campaign ID'] || row.campaignId || `campaign-${index}`,
        ),
        campaignName: String(
          row['Campaign Name'] || row.campaignName || 'Unknown Campaign',
        ),
        mediaGroup: row['Media Group'] || row.mediaGroup,
        media: row.Media || row.media,
        impressions: parseNumber(row.Impressions || row.impressions),
        spend: parseNumber(row.Spend || row.spend) || 0,
        source: row.Source || row.source || fileName,
        lineItemName: row['Line Item'] || row.lineItemName,
        fileId: `preview-${Date.now()}`,
      } as UploadedMonthlyData;
    })
    .filter((item): item is UploadedMonthlyData => item !== null);
};

/**
 * Parse offline data from Excel rows
 */
const parseOfflineData = (
  rows: unknown[],
  fileName: string,
): UploadedOfflineData[] => {
  return (rows as Record<string, unknown>[])
    .map((row, index) => {
      const periodStartDate = parseDate(
        row['Period Start'] ||
          row['Period Start Date'] ||
          row.periodStartDate ||
          row.Date ||
          row.date,
      );
      const periodEndDate = parseDate(
        row['Period End'] ||
          row['Period End Date'] ||
          row.periodEndDate ||
          periodStartDate,
      );
      if (!periodStartDate) return null;

      return {
        periodStartDate: periodStartDate,
        periodEndDate: periodEndDate || periodStartDate,
        campaignId: String(
          row['Campaign ID'] || row.campaignId || `campaign-${index}`,
        ),
        campaignName: String(
          row['Campaign Name'] || row.campaignName || 'Unknown Campaign',
        ),
        mediaGroup: row['Media Group'] || row.mediaGroup,
        media: row.Media || row.media,
        trps: parseNumber(row.TRPs || row.trps),
        spend: parseNumber(row.Spend || row.spend) || 0,
        source: row.Source || row.source || fileName,
        spotName: row['Spot Name'] || row.spotName,
        fileId: `preview-${Date.now()}`,
      } as UploadedOfflineData;
    })
    .filter((item): item is UploadedOfflineData => item !== null);
};

/**
 * Parse KPI data from Excel rows
 */
const parseKpiData = (rows: unknown[], fileName: string): UploadedKpiData[] => {
  return (rows as Record<string, unknown>[])
    .map((row) => {
      const date = parseDate(row.Date || row.date);
      if (!date) return null;

      return {
        date: date,
        period: String(row.Period || row.period || 'daily'),
        category: String(row.Category || row.category || 'general'),
        channelSource: String(
          row['Channel/Source'] || row.channelSource || row.source || fileName,
        ),
        sales: parseNumber(row.Sales || row.sales),
        unitsSold: parseNumber(row['Units Sold'] || row.unitsSold),
        customersRegistrations: parseNumber(
          row['Customer Registrations'] || row.customersRegistrations,
        ),
        fileId: `preview-${Date.now()}`,
      } as UploadedKpiData;
    })
    .filter((item): item is UploadedKpiData => item !== null);
};

/**
 * Helper function to check if a row has specific columns
 */
const hasColumn = (
  row: Record<string, unknown>,
  columnNames: string[],
): boolean => {
  return columnNames.some((name) =>
    Object.prototype.hasOwnProperty.call(row, name),
  );
};

/**
 * Parse date from various formats
 */
const parseDate = (value: unknown): string | null => {
  if (!value) return null;

  try {
    // If it's already a Date object
    if (value instanceof Date) {
      return value.toISOString().split('T')[0];
    }

    // If it's a string
    if (typeof value === 'string') {
      const parsed = new Date(value);
      if (!isNaN(parsed.getTime())) {
        return parsed.toISOString().split('T')[0];
      }
    }

    // If it's an Excel serial date number
    if (typeof value === 'number') {
      const excelDate = XLSX.SSF.parse_date_code(value);
      if (excelDate) {
        const date = new Date(excelDate.y, excelDate.m - 1, excelDate.d);
        return date.toISOString().split('T')[0];
      }
    }

    return null;
  } catch {
    return null;
  }
};

/**
 * Parse number from various formats
 */
const parseNumber = (value: unknown): number | undefined => {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }

  if (typeof value === 'number') {
    return value;
  }

  if (typeof value === 'string') {
    // Remove common formatting characters
    const cleaned = value.replace(/[€$,\s]/g, '');
    const parsed = parseFloat(cleaned);
    return isNaN(parsed) ? undefined : parsed;
  }

  return undefined;
};

/**
 * Parse multiple Excel files and combine their data
 */
export const parseMultipleExcelFiles = async (
  files: File[],
): Promise<UploadedDataResponse['data']> => {
  const parsedFiles = await Promise.all(
    files.map((file) => parseExcelFile(file)),
  );

  // Combine all parsed data
  const combined: UploadedDataResponse['data'] = {
    fileInfo: {
      fileId: `preview-${Date.now()}`,
      fileKey: `preview/${files.map((f) => f.name).join('_')}`,
      fileName: files.map((f) => f.name).join(', '),
      status: 'succeeded',
      uploadedAt: new Date().toISOString(),
    },
    dailyData: [],
    weeklyData: [],
    monthlyData: [],
    offlineData: [],
    kpiData: [],
    summary: {
      totalRecords: 0,
      dateRange: {},
      dataTypes: [],
    },
  };

  // Combine data from all files
  parsedFiles.forEach((parsed) => {
    combined.dailyData.push(...parsed.dailyData);
    combined.weeklyData.push(...parsed.weeklyData);
    combined.monthlyData.push(...parsed.monthlyData);
    combined.offlineData.push(...parsed.offlineData);
    combined.kpiData.push(...parsed.kpiData);
  });

  // Calculate summary
  combined.summary.totalRecords =
    combined.dailyData.length +
    combined.weeklyData.length +
    combined.monthlyData.length +
    combined.offlineData.length +
    combined.kpiData.length;

  // Determine data types
  const dataTypes = new Set<string>();
  if (combined.dailyData.length > 0) dataTypes.add('daily');
  if (combined.weeklyData.length > 0) dataTypes.add('weekly');
  if (combined.monthlyData.length > 0) dataTypes.add('monthly');
  if (combined.offlineData.length > 0) dataTypes.add('offline');
  if (combined.kpiData.length > 0) dataTypes.add('kpi');
  combined.summary.dataTypes = Array.from(dataTypes);

  // Calculate date range
  const allDates: Date[] = [];
  combined.dailyData.forEach((d) => {
    if (d.date) allDates.push(new Date(d.date));
  });
  combined.weeklyData.forEach((d) => {
    if (d.weekStartDate) allDates.push(new Date(d.weekStartDate));
  });
  combined.monthlyData.forEach((d) => {
    if (d.monthStartDate) allDates.push(new Date(d.monthStartDate));
  });
  combined.offlineData.forEach((d) => {
    if (d.periodStartDate) allDates.push(new Date(d.periodStartDate));
  });
  combined.kpiData.forEach((d) => {
    if (d.date) allDates.push(new Date(d.date));
  });

  if (allDates.length > 0) {
    const validDates = allDates.filter((d) => !isNaN(d.getTime()));
    if (validDates.length > 0) {
      validDates.sort((a, b) => a.getTime() - b.getTime());
      combined.summary.dateRange.earliest = validDates[0]
        .toISOString()
        .split('T')[0];
      combined.summary.dateRange.latest = validDates[validDates.length - 1]
        .toISOString()
        .split('T')[0];
    }
  }

  return combined;
};
